import { SearchRounded } from "@mui/icons-material";
import {
  Box,
  Button,
  InputAdornment,
  Modal,
  Paper,
  TextField,
  Typography,
  alpha,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";

import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import StyledDataGrid from "../../../../components/StyledDataGrid";
import { Toast } from "../../../../components/Toast";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import {
  ArrayEditor,
  BooleanKeyValueEditor,
  KeyValueEditor,
  TimeZoneArrayEditor,
} from "./PlatformSecretEditors";

const convertBooleanStrings = (value) => {
  if (value === "true") return true;
  if (value === "false") return false;
  try {
    const parsed = JSON.parse(value);
    if (
      typeof parsed === "object" &&
      parsed !== null &&
      !Array.isArray(parsed)
    ) {
      return JSON.stringify(
        Object.fromEntries(
          Object.entries(parsed).map(([k, v]) => [
            k,
            v === "true" ? true : v === "false" ? false : v,
          ])
        )
      );
    }
  } catch {}
  return value;
};

const getFieldType = (fieldName, value) => {
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);

      if (Array.isArray(parsed)) return "array";

      if (typeof parsed === "object" && parsed !== null) {
        const values = Object.values(parsed);

        if (
          values.length > 0 &&
          values.every((v) => v === "true" || v === "false" || typeof v === "boolean")
        ) {
          return "boolean_keyvalue";
        }

        if (values.length > 0 && values.every((v) => Array.isArray(v))) {
          return "timezone_array";
        }

        return "keyvalue";
      }
    } catch {
      return "string";
    }
  }
  return "string";
};

const formatValue = (value, fieldName) => {
  const fieldType = getFieldType(fieldName, value);

  if (fieldType === "boolean_keyvalue" || fieldType === "keyvalue") {
    try {
      const parsed = typeof value === "string" ? JSON.parse(value) : value;
      const count = Object.keys(parsed || {}).length;
      return `${count} ${count === 1 ? "entry" : "entries"}`;
    } catch {
      return "Invalid data";
    }
  }

  if (fieldType === "array") {
    try {
      const parsed = typeof value === "string" ? JSON.parse(value) : value;
      const count = Array.isArray(parsed) ? parsed.length : 0;
      return `${count} ${count === 1 ? "item" : "items"}`;
    } catch {
      return "Invalid data";
    }
  }

  if (fieldType === "timezone_array") {
    try {
      const parsed = typeof value === "string" ? JSON.parse(value) : value;
      const tzCount = Object.keys(parsed || {}).length;
      const totalCustomers = Object.values(parsed || {}).reduce(
        (sum, arr) => sum + (Array.isArray(arr) ? arr.length : 0),
        0
      );
      return `${tzCount} timezones, ${totalCustomers} customers`;
    } catch {
      return "Invalid data";
    }
  }

  if (typeof value === "string") {
    if (value.length > 100) {
      return value.substring(0, 100) + "...";
    }
    return value;
  }
  const stringValue = JSON.stringify(value);
  if (stringValue.length > 100) {
    return stringValue.substring(0, 100) + "...";
  }
  return stringValue;
};

const ManagePlatformSecretTable = () => {
  const colors = useColors();
  const { fetchPlatformSecret, updatePlatformSecretField } = useAPI();

  const [platformSecret, setPlatformSecret] = useState({});
  const [rows, setRows] = useState([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedField, setSelectedField] = useState(null);
  const [editedValue, setEditedValue] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const fetchSecret = async () => {
      try {
        const response = await fetchPlatformSecret();
        setPlatformSecret(response.data);
      } catch (error) {
        Toast.error("Error fetching platform secret");
      }
    };
    fetchSecret();
  }, []);

  useEffect(() => {
    const newRows = Object.entries(platformSecret).map(
      ([field, value], idx) => ({
        id: idx,
        field,
        value: formatValue(value, field),
        rawValue: value,
        fieldType: getFieldType(field, value),
      })
    );
    setRows(newRows);
  }, [platformSecret]);

  const filteredRows = useMemo(() => {
    if (!searchTerm) return rows;
    const term = searchTerm.toLowerCase();
    return rows.filter(
      (row) =>
        row.field.toLowerCase().includes(term) ||
        row.value.toLowerCase().includes(term) ||
        row.fieldType.toLowerCase().includes(term)
    );
  }, [rows, searchTerm]);

  const handleEditClick = (field, value) => {
    setSelectedField(field);
    const stringValue =
      typeof value === "string" ? value : JSON.stringify(value);
    setEditedValue(stringValue);
    setIsEditModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsEditModalOpen(false);
    setSelectedField(null);
    setEditedValue("");
  };

  const handleSaveClick = async () => {
    try {
      const valueToSave = convertBooleanStrings(editedValue);
      await updatePlatformSecretField(selectedField, valueToSave);

      const response = await fetchPlatformSecret();
      setPlatformSecret(response.data);
      Toast.success("Platform secret field updated successfully!");

      handleCloseModal();
    } catch (error) {
      Toast.error(`Error updating platform secret: ${error.message}`);
    }
  };

  const columns = [
    {
      field: "field",
      headerName: "Field Name",
      width: 250,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="bold">
          {params.value}
        </Typography>
      ),
    },
    {
      field: "value",
      headerName: "Value",
      width: 350,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: "fieldType",
      headerName: "Type",
      width: 150,
      renderCell: (params) => {
        const typeLabels = {
          string: "Text",
          keyvalue: "Object",
          boolean_keyvalue: "Feature Flags",
          array: "List",
          timezone_array: "Timezone Map",
        };
        return (
          <Typography variant="caption" color="text.secondary">
            {typeLabels[params.value] || params.value}
          </Typography>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      renderCell: (params) => {
        return (
          <SecondaryButton
            onClick={() =>
              handleEditClick(params.row.field, params.row.rawValue)
            }
            size="small"
          >
            Edit
          </SecondaryButton>
        );
      },
    },
  ];

  return (
    <Box sx={{ marginTop: "16px", width: "90%", maxWidth: "900px" }}>
      <Paper
        sx={{
          padding: 3,
          borderRadius: 3,
          border: `1px solid ${colors.divider}`,
          mb: 2,
        }}
      >
        <Typography
          variant="h6"
          gutterBottom
          sx={{ fontWeight: 500, color: colors.text_primary }}
        >
          Manage Platform Secret
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Edit environment-specific platform configuration stored in AWS Secrets
          Manager. Changes take effect immediately but may require cache clear.
        </Typography>
      </Paper>

      <Box sx={{ mb: 2, width: "300px" }}>
        <TextField
          InputProps={{
            sx: {
              color: colors.contrast_text_dark,
              fontSize: "14px",
            },
            startAdornment: (
              <InputAdornment position="start">
                <SearchRounded
                  style={{
                    color: colors.text_disabled,
                    width: "16px",
                    height: "16px",
                  }}
                />
              </InputAdornment>
            ),
          }}
          placeholder="Search fields"
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{
            backgroundColor: colors.common_white,
            "& .MuiOutlinedInput-notchedOutline": {
              borderRadius: "8px",
              borderColor: colors.grey_32percent,
            },
            "& .MuiOutlinedInput-root": {
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                border: "0.5px solid " + colors.divider2,
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                border: "0.5px solid " + colors.grey_500,
              },
            },
            "& .MuiInputBase-input::placeholder": {
              color: colors.text_primary,
            },
            width: "100%",
            "& .MuiInputBase-root": { height: "32px" },
          }}
        />
      </Box>

      <StyledDataGrid
        rows={filteredRows}
        columns={columns}
        pageSize={10}
        rowsPerPageOptions={[10]}
        autoHeight
        sx={{
          border: `1px solid ${colors.divider}`,
          borderRadius: "8px",
          "& .MuiDataGrid-cell": {
            padding: "8px",
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: alpha(colors.primary_main, 0.03),
            borderBottom: `1px solid ${colors.divider}`,
          },
        }}
      />

      <Modal open={isEditModalOpen} onClose={handleCloseModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 700,
            maxHeight: "80vh",
            bgcolor: colors.common_white,
            boxShadow: 24,
            borderRadius: 3,
            border: `1px solid ${colors.divider}`,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box sx={{ p: 4, pb: 2 }}>
            <Typography
              variant="h6"
              component="h2"
              gutterBottom
              sx={{ fontWeight: 500, color: colors.text_primary }}
            >
              Edit Platform Secret Field
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 1,
                p: 1.5,
                backgroundColor: alpha(colors.primary_main, 0.03),
                borderRadius: 2,
                border: `1px solid ${alpha(colors.primary_main, 0.1)}`,
              }}
            >
              Field: <strong>{selectedField}</strong>
            </Typography>
          </Box>

          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              px: 4,
              pb: 2,
            }}
          >
            {selectedField &&
              getFieldType(selectedField, editedValue) ===
                "boolean_keyvalue" && (
                <BooleanKeyValueEditor
                  data={editedValue}
                  onChange={setEditedValue}
                />
              )}
            {selectedField &&
              getFieldType(selectedField, editedValue) === "keyvalue" && (
                <KeyValueEditor data={editedValue} onChange={setEditedValue} />
              )}
            {selectedField &&
              getFieldType(selectedField, editedValue) === "array" && (
                <ArrayEditor data={editedValue} onChange={setEditedValue} />
              )}
            {selectedField &&
              getFieldType(selectedField, editedValue) === "timezone_array" && (
                <TimeZoneArrayEditor
                  data={editedValue}
                  onChange={setEditedValue}
                />
              )}
            {selectedField &&
              getFieldType(selectedField, editedValue) === "string" && (
                <TextField
                  fullWidth
                  multiline
                  rows={8}
                  label="Value"
                  value={editedValue}
                  onChange={(e) => setEditedValue(e.target.value)}
                  sx={{
                    "& textarea": {
                      fontFamily: "monospace",
                      fontSize: "0.875rem",
                    },
                  }}
                />
              )}
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              p: 4,
              pt: 2,
              borderTop: `1px solid ${colors.divider}`,
              bgcolor: colors.common_white,
            }}
          >
            <Button
              onClick={handleCloseModal}
              variant="outlined"
              sx={{
                borderRadius: 2,
                textTransform: "none",
                borderColor: colors.grey_32percent,
                color: colors.text_primary,
                "&:hover": {
                  borderColor: colors.grey_500,
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveClick}
              variant="contained"
              disabled={
                typeof editedValue === "string" ? !editedValue.trim() : false
              }
              sx={{
                borderRadius: 2,
                textTransform: "none",
                backgroundColor: colors.primary_main,
                "&:hover": {
                  backgroundColor: colors.primary_dark,
                },
              }}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default ManagePlatformSecretTable;
